const {
  SignupRequest,
  RequestRemovalRequest,
  ValidateSignupRequest,
  FinaliseSignupRequest,
  BeginAuthRequest,
  FinaliseAuthRequest,
  RemoveUserRequest,
  ChangeBackgroundRequest,
  LogoutRequest,
  GenerateAgentTokenRequest,
  ListPasskeysRequest,
  RemovePasskeyRequest,
  CancelRecoveryRequest,
} = require("./register_pb.js");
const {
  RegROServicePromiseClient,
  RegRWServicePromiseClient,
} = require("./register_grpc_web_pb.js");
const { UserDataRequest } = require("./weber_pb.js");
const { WeberServicePromiseClient } = require("./weber_grpc_web_pb.js");

const API_BASE = window.BOUNCER_API_BASE || "https://www.pitican.com:443";
const DEFAULT_BACKGROUND = "linear-gradient(135deg, #4338ca 0%, #0ea5e9 100%)";
const GRPC_ALREADY_EXISTS = 6;
// The apps this page can sign in to, generated from the server registry
// (scripts/generate-popup-origins.py); contract.test.js keeps it in step.
const APP_CATALOG = require("./app_catalog.json");
const APP_CONFIGS = Object.fromEntries(
  APP_CATALOG.map((app) => [app.appId, { key: app.appId, label: app.label, appId: app.appId }])
);
// Older links used ?app=alpha and ?app=beta.
const LEGACY_APP_KEYS = {
  alpha: "bouncer-app-7f4d9a3c2b1e5f68",
  beta: "bouncer-app-4d1e8f2a6c9b3d57",
};
const LAST_APP_KEY = "bouncer:simulator:lastApp";
// Every app signs in against this one RP ID, whichever domain the page is on (the other
// domains are related origins, listed in /.well-known/webauthn).
const RP_ID = "pitican.com";
const RP_ORIGINS = ["https://pitican.com", "https://www.pitican.com"];
const SESSION_APP_SCOPE_KEY = "piticanSessionAppId";

const grpcOptions = { withCredentials: false };
const regROService = new RegROServicePromiseClient(API_BASE, null, grpcOptions);
const regRWService = new RegRWServicePromiseClient(API_BASE, null, grpcOptions);
const weberService = new WeberServicePromiseClient(API_BASE, null, grpcOptions);

const state = {
  webAuthnAuthInProgress: false,
  autoRetryHandlerAttached: false,
  ignoreCredentialHint: false,
  lastEnrollmentFlow: "",
  conditionalAbort: null,
};

// The credential ID of the passkey last used for this app narrows the system chooser.
// It is a convenience only; storage may be unavailable or cleared, so every access is guarded.
const credentialHintKey = (appId) => `bouncer:${appId}:credentialId`;

function getCredentialHint(appId) {
  try {
    const value = localStorage.getItem(credentialHintKey(appId)) || "";
    return /^[A-Za-z0-9_-]{1,1300}$/.test(value) ? value : "";
  } catch {
    return "";
  }
}

function saveCredentialHint(appId, credentialId) {
  try {
    if (appId && /^[A-Za-z0-9_-]{1,1300}$/.test(credentialId || "")) {
      localStorage.setItem(credentialHintKey(appId), credentialId);
    }
  } catch { /* hint is optional */ }
}

window.hasCredentialHint = (appId) => Boolean(getCredentialHint(appId));

function debugLog(message, details) {
  if (typeof details === "undefined") {
    console.log(`[frontend] ${message}`);
    return;
  }
  console.log(`[frontend] ${message}`, details);
}

function byId(id) {
  return document.getElementById(id);
}

function query(selector) {
  return document.querySelector(selector);
}

function isTokenExpired(token) {
  try {
    const payload = decodeJwtPayload(token);
    if (!payload) {
      return true;
    }
    return typeof payload.exp === "number" && payload.exp < Date.now() / 1000;
  } catch {
    return true;
  }
}

function clearExpiredLegacyToken() {
  const token = localStorage.getItem("piticanToken");
  if (token && isTokenExpired(token)) {
    localStorage.removeItem("piticanToken");
    return true;
  }
  return false;
}

function clearLegacyAuthState() {
  localStorage.removeItem("piticanToken");
  localStorage.removeItem("piticanUserId");
  sessionStorage.removeItem("piticanToken");
  sessionStorage.removeItem("piticanUserId");
  clearCookie("piticanUserId");
}

function clearStoredSessionAppId() {
  sessionStorage.removeItem(`bouncer:${getCurrentAppConfig().appId}:accessToken`);
  localStorage.removeItem(SESSION_APP_SCOPE_KEY);
}

function clearCookie(name) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

function getCookie(name) {
  const prefix = `${name}=`;
  return document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
    ?.slice(prefix.length) || "";
}

function isHomePage() {
  return Boolean(byId("userTitle"));
}

function isRegisterPage() {
  return Boolean(byId("registerTitle"));
}

function isUnregisterPage() {
  return Boolean(byId("unregisterTitle"));
}

function showDialog(selector) {
  const element = query(selector);
  if (element) {
    element.classList.add("active");
  }
}

function hideDialog(selector) {
  const element = query(selector);
  if (element) {
    element.classList.remove("active");
  }
}

function setError(selector, message) {
  const element = query(selector);
  if (!element) {
    return;
  }
  element.textContent = message;
  element.classList.add("active");
  // The error line can sit below the visible part of a scrolling dialog; bring it into view.
  if (typeof element.scrollIntoView === "function") {
    element.scrollIntoView({ block: "nearest" });
  }
}

function showTryAgainDialog(message) {
  hideDialog(".popupWebauthn");
  if (message) {
    setError(".errorTryagain", `Error [${message}]`);
  } else {
    clearError(".errorTryagain");
  }
  showDialog(".popupTryagain");
}

function clearError(selector) {
  const element = query(selector);
  if (!element) {
    return;
  }
  element.textContent = "";
  element.classList.remove("active");
}

function setBodyBackground(background) {
  document.body.style.background = background || DEFAULT_BACKGROUND;
}

function updateColorPickerPreview(value) {
  const normalizedValue = value || "#3a7bd5";
  const swatch = query(".colorPickerSwatch");
  const label = byId("colorPickerValue");
  if (swatch) {
    swatch.style.background = normalizedValue;
  }
  if (label) {
    label.innerText = normalizedValue;
  }
}

function setStatusLine(message) {
  if (isHomePage()) {
    byId("userTitle").textContent = message;
  }
}

function addDebugLine(message) {
  const contentContainer = byId("contentContainer");
  if (!contentContainer) {
    return;
  }

  const line = document.createElement("p");
  line.textContent = message;
  contentContainer.appendChild(line);
}

function initialAppId() {
  const requested = new URLSearchParams(window.location.search).get("app") || "";
  const fromQuery = LEGACY_APP_KEYS[requested] || requested;
  if (APP_CONFIGS[fromQuery]) return fromQuery;
  try {
    const remembered = localStorage.getItem(LAST_APP_KEY);
    if (APP_CONFIGS[remembered]) return remembered;
  } catch {}
  return APP_CATALOG[0].appId;
}

let currentAppId = initialAppId();

// A pitican.com page can act as any app in the catalog, so the app is a choice, not a constant.
function setCurrentApp(appId) {
  currentAppId = appId;
  try { localStorage.setItem(LAST_APP_KEY, appId); } catch {}
}

function getCurrentAppConfig() {
  return APP_CONFIGS[currentAppId] || { key: currentAppId, label: currentAppId, appId: currentAppId };
}

function getAppConfigById(appId) {
  return APP_CONFIGS[appId] || null;
}

function getStoredSessionAppId() {
  return getStoredAccessToken() ? getCurrentAppConfig().appId : "";
}

function persistTokenScope(token) {
  const payload = decodeJwtPayload(token);
  const appId = payload?.app_id || "";

  debugLog("Decoded authentication token payload", payload);

  if (!appId) {
    throw new Error("Authentication token did not include an app scope.");
  }

  sessionStorage.setItem(`bouncer:${appId}:accessToken`, token);
  debugLog("Persisted token app scope", { appId });
}

function hasSession(appId) {
  try {
    const token = sessionStorage.getItem(`bouncer:${appId}:accessToken`) || "";
    return Boolean(token) && !isTokenExpired(token);
  } catch { return false; }
}

// The picker, the badge and the chips of apps that already have a session in this tab.
function renderAppChrome() {
  if (!isHomePage()) {
    return;
  }
  const appConfig = getCurrentAppConfig();
  const select = byId("appSelect");
  if (select) select.value = appConfig.appId;
  const badge = byId("appBadge");
  if (badge) badge.textContent = appConfig.label.slice(0, 1).toUpperCase();
  const notice = byId("activeAppNotice");
  if (notice) notice.textContent = `Acting as ${appConfig.label}. This page behaves like that app's own sign-in.`;
  document.title = `${appConfig.label} - Bouncer`;

  const chips = byId("sessionChips");
  if (chips) {
    chips.replaceChildren();
    for (const app of APP_CATALOG) {
      if (!hasSession(app.appId)) continue;
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "chip" + (app.appId === appConfig.appId ? " current" : "");
      chip.textContent = app.label;
      chip.title = `Signed in to ${app.label}`;
      chip.addEventListener("click", () => window.selectApp(app.appId));
      chips.appendChild(chip);
    }
    chips.hidden = chips.childElementCount === 0;
  }
}

// What the app itself would receive after a sign-in: its own opaque user ID, never the account's.
function renderClaims(email) {
  const list = byId("claimsList");
  if (!list) return;
  list.replaceChildren();
  const payload = decodeJwtPayload(getStoredAccessToken()) || {};
  const rows = [
    ["Email", email || "(not shared)"],
    ["User ID (sub)", payload.sub || ""],
    ["App", payload.app_id || ""],
    ["Audience", Array.isArray(payload.aud) ? payload.aud.join(", ") : payload.aud || ""],
    ["Grant", payload.grant_id || ""],
    ["Expires", typeof payload.exp === "number" ? new Date(payload.exp * 1000).toLocaleString() : ""],
  ];
  for (const [name, value] of rows) {
    if (!value) continue;
    const term = document.createElement("dt");
    term.textContent = name;
    const detail = document.createElement("dd");
    detail.textContent = value;
    list.append(term, detail);
  }
}

function setProtoStringFieldIfAvailable(message, setterName, value) {
  if (message && typeof message[setterName] === "function") {
    message[setterName](value);
  }
}

function assertTokenMatchesCurrentApp(token, flowName) {
  const payload = decodeJwtPayload(token);
  const currentApp = getCurrentAppConfig();
  const payloadAppId = payload?.app_id || "";
  const payloadAudience = payload?.aud || "";
  const hasAudienceMatch = Array.isArray(payloadAudience)
    ? payloadAudience.includes(currentApp.appId)
    : payloadAudience === currentApp.appId;

  if (payloadAppId !== currentApp.appId || hasAudienceMatch !== true) {
    throw new Error(
      `${flowName} returned a token for the wrong app. Expected ${currentApp.appId}.`
    );
  }

  debugLog(`${flowName} token matches active app`, {
    appKey: currentApp.key,
    appId: currentApp.appId,
  });
}

function showPanels(signedIn) {
  const out = byId("signedOutPanel");
  const inn = byId("signedInPanel");
  if (out) out.hidden = signedIn;
  if (inn) inn.hidden = !signedIn;
}

function renderLoggedOutState() {
  if (!isHomePage()) {
    return;
  }

  const appConfig = getCurrentAppConfig();

  const loginButton = byId("showWebAuthnLogin");
  if (loginButton) {
    loginButton.textContent = "Login";
    loginButton.classList.remove("inactive");
  }

  showPanels(false);
  setBodyBackground(DEFAULT_BACKGROUND);
  setStatusLine(`Who are you for ${appConfig.label}?`);
  renderAppChrome();
}

function renderAuthenticatedState({ email, background }) {
  if (!isHomePage()) {
    return;
  }

  const appConfig = getCurrentAppConfig();

  const loginButton = byId("showWebAuthnLogin");
  if (loginButton) {
    loginButton.textContent = "Logout";
    loginButton.classList.add("inactive");
  }

  cancelConditionalLogin();
  showPanels(true);
  setBodyBackground(background);
  setStatusLine(
    email
      ? `Welcome ${email} to ${appConfig.label}`
      : `Welcome to ${appConfig.label}`
  );
  renderClaims(email);
  renderAppChrome();
}

function toArrayBuffer(base64urlValue) {
  const base64Value = base64urlValue.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64Value + "=".repeat((4 - (base64Value.length % 4)) % 4);
  const binaryString = atob(padded);
  const bytes = new Uint8Array(binaryString.length);
  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }
  return bytes.buffer;
}

function arrayBufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let index = 0; index < bytes.byteLength; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function publicKeyCredentialToJSON(value) {
  if (value instanceof Array) {
    return value.map(publicKeyCredentialToJSON);
  }

  if (value instanceof ArrayBuffer) {
    return arrayBufferToBase64Url(value);
  }

  if (ArrayBuffer.isView(value)) {
    return arrayBufferToBase64Url(
      value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength)
    );
  }

  if (value && typeof value.toJSON === "function") {
    return publicKeyCredentialToJSON(value.toJSON());
  }

  if (value && typeof value === "object") {
    const result = {};
    for (const key in value) {
      result[key] = publicKeyCredentialToJSON(value[key]);
    }
    return result;
  }

  return value;
}

function stringToBase64Url(value) {
  return btoa(unescape(encodeURIComponent(value)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function decodeBase64ToString(value) {
  const binaryString = atob(value);
  const bytes = new Uint8Array(binaryString.length);
  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }
  return new TextDecoder("utf-8").decode(bytes);
}

function decodeJwtPayload(token) {
  if (!token) {
    return null;
  }

  const parts = token.split(".");
  if (parts.length < 2) {
    return null;
  }

  try {
    const base64Value = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64Value + "=".repeat((4 - (base64Value.length % 4)) % 4);
    return JSON.parse(decodeBase64ToString(padded));
  } catch (error) {
    debugLog("Unable to decode JWT payload for client-side signaling", {
      message: error?.message || String(error),
    });
    return null;
  }
}

// Decodes the options the server sends. Browsers with the WebAuthn Level 3 JSON helpers
// parse them natively; older ones fall back to decoding the binary fields by hand.
// `create` selects registration (true) or authentication (false) options. `hints` are
// advisory presentation hints for the browser's passkey dialog.
function decodePublicKeyOptions(encodedChallenge, options = {}) {
  const parsed = JSON.parse(decodeBase64ToString(encodedChallenge));
  const create = Boolean(options.create);
  const nativeParser = window.PublicKeyCredential
    ? (create
        ? PublicKeyCredential.parseCreationOptionsFromJSON
        : PublicKeyCredential.parseRequestOptionsFromJSON)
    : undefined;
  if (typeof nativeParser === "function") {
    try {
      parsed.publicKey = nativeParser.call(PublicKeyCredential, parsed.publicKey);
      if (options.hints && options.hints.length) {
        parsed.publicKey.hints = options.hints;
      }
      return parsed;
    } catch (error) {
      debugLog("Native options parsing failed; decoding by hand", {
        message: error?.message || String(error),
      });
    }
  }

  const publicKey = parsed.publicKey;
  publicKey.challenge = toArrayBuffer(publicKey.challenge);
  if (create && publicKey.user?.id) {
    publicKey.user.id = toArrayBuffer(publicKey.user.id);
  }
  if (create && publicKey.excludeCredentials) {
    publicKey.excludeCredentials = publicKey.excludeCredentials.map((credential) => ({
      ...credential,
      id: toArrayBuffer(credential.id),
    }));
  }
  if (publicKey.allowCredentials) {
    publicKey.allowCredentials = publicKey.allowCredentials.map((credential) => ({
      ...credential,
      id: toArrayBuffer(credential.id),
    }));
  }
  if (options.hints && options.hints.length) {
    publicKey.hints = options.hints;
  }
  return parsed;
}

function serializeCredential(credential) {
  const serialized = JSON.stringify(publicKeyCredentialToJSON(credential));
  debugLog("Serialized credential payload", {
    jsonLength: serialized.length,
    preview: serialized.slice(0, 120),
  });
  return stringToBase64Url(serialized);
}

async function requestSignup(email) {
  const appConfig = getCurrentAppConfig();
  debugLog("Requesting signup email", { email, appId: appConfig.appId });
  const request = new SignupRequest();
  request.setEmail(email.toLowerCase());
  setProtoStringFieldIfAvailable(request, "setAppid", appConfig.appId);
  await regRWService.requestEntry(request, {});
}

async function requestRemovalLink(deleteAccount) {
  debugLog("Requesting removal confirmation email", { deleteAccount });
  const request = new RequestRemovalRequest();
  request.setToken(getStoredAccessToken());
  request.setDeleteaccount(Boolean(deleteAccount));
  await regRWService.requestRemoval(request, {});
}

async function finalizeSignup(attestation, registrationHandle) {
  debugLog("Finalizing signup", {
    registrationHandle,
    attestationLength: attestation.length,
  });
  const request = new FinaliseSignupRequest();
  request.setAttestation(attestation);
  request.setRegistrationhandle(registrationHandle);
  const response = await regRWService.addEntry(request, {});
  const accessToken = response.getAccesstoken();
  assertTokenMatchesCurrentApp(accessToken, "Registration");
  persistTokenScope(accessToken);
  return accessToken;
}

async function startAuth({ useHint = true } = {}) {
  const appConfig = getCurrentAppConfig();
  const hint = !useHint || state.ignoreCredentialHint ? "" : getCredentialHint(appConfig.appId);
  debugLog("Starting discoverable authentication", { appId: appConfig.appId, hinted: Boolean(hint) });
  const request = new BeginAuthRequest();
  setProtoStringFieldIfAvailable(request, "setAppid", appConfig.appId);
  if (hint) request.setCredentialid(hint);
  const response = await regROService.startAuthn(request, {});
  debugLog("Received StartAuthn challenge", {
    challengeLength: response.getChallenge().length,
  });
  return response.getChallenge();
}

async function finalizeAuth(attestation, userHandle, challenge, enrollApp = false) {
  debugLog("Finalizing authentication", {
    userHandleLength: userHandle.length,
    challengeLength: challenge.length,
    attestationLength: attestation.length,
  });
  const request = new FinaliseAuthRequest();
  request.setAttestation(attestation);
  request.setUserhandle(userHandle);
  request.setChallenge(challenge);
  request.setEnrollapp(Boolean(enrollApp));
  const response = await regROService.endAuthn(request, {});
  const accessToken = response.getAccesstoken();
  assertTokenMatchesCurrentApp(accessToken, "Authentication");
  persistTokenScope(accessToken);
  return accessToken;
}

function getStoredAccessToken() {
  const key = `bouncer:${getCurrentAppConfig().appId}:accessToken`;
  const token = sessionStorage.getItem(key) || "";
  if (token && isTokenExpired(token)) { sessionStorage.removeItem(key); return ""; }
  return token;
}

async function getUserData() {
  debugLog("Fetching authenticated user data", {
    usesExplicitAppToken: true,
  });
  const request = new UserDataRequest();
  request.setToken(getStoredAccessToken());
  const response = await weberService.getUserData(request, {});
  return {
    email: response.getEmail(),
    background: response.getBackground(),
  };
}

async function persistBackground(color) {
  debugLog("Persisting background color", { color });
  const request = new ChangeBackgroundRequest();
  request.setToken(getStoredAccessToken());
  request.setBackground(color);
  await regRWService.changeBackground(request, {});
}

async function deleteUser(token) {
  debugLog("Removing user via unregister page");
  const request = new RemoveUserRequest();
  request.setToken(token || "");
  const response = await regRWService.removeEntry(request, {});
  return { email: response.getEmail(), userHandle: response.getUserhandle() };
}

async function logoutSession() {
  debugLog("Requesting backend logout");
  const request = new LogoutRequest();
  await regRWService.logoutSession(request, {});
}

// The WebAuthn Signal API lets the site tell a browser's passkey provider what is still
// valid, so deleted passkeys stop showing up in its chooser. Every call is best effort:
// the provider decides what to do and unsupported browsers simply have nothing to do.
function signalsAvailable(name) {
  return Boolean(window.PublicKeyCredential) && typeof PublicKeyCredential[name] === "function";
}

async function signalAccountDeleted(userHandle) {
  if (!userHandle || !signalsAvailable("signalAllAcceptedCredentials")) {
    return {
      attempted: false,
      message: "Delete the saved passkey from your password manager because this account is no longer valid.",
    };
  }
  try {
    await PublicKeyCredential.signalAllAcceptedCredentials({
      rpId: RP_ID,
      userId: userHandle,
      allAcceptedCredentialIds: [],
    });
    return {
      attempted: true,
      message: "This browser was told that the passkey is no longer valid. If it still appears, delete it from your password manager.",
    };
  } catch (error) {
    debugLog("Passkey deletion signal failed", { message: error?.message || String(error) });
    return {
      attempted: true,
      message: "The account was deleted, but you may still need to remove the saved passkey manually from your password manager.",
    };
  }
}

async function signalUnknownPasskey(credentialId) {
  if (!credentialId || !signalsAvailable("signalUnknownCredential")) return;
  try {
    await PublicKeyCredential.signalUnknownCredential({ rpId: RP_ID, credentialId });
  } catch (error) {
    debugLog("Unknown passkey signal failed", { message: error?.message || String(error) });
  }
}

async function signalAcceptedPasskeys(token) {
  if (!signalsAvailable("signalAllAcceptedCredentials")) return;
  try {
    const list = await listPasskeys(token);
    if (!list.userHandle) return;
    await PublicKeyCredential.signalAllAcceptedCredentials({
      rpId: RP_ID,
      userId: list.userHandle,
      allAcceptedCredentialIds: list.passkeys.map((passkey) => passkey.credentialId),
    });
  } catch (error) {
    debugLog("Accepted passkeys signal failed", { message: error?.message || String(error) });
  }
}

function isUnknownCredentialError(error) {
  return /UNKNOWN_CREDENTIAL/.test(error?.message || "");
}

function isAppNotEnrolledError(error) {
  return /APP_NOT_ENROLLED/.test(error?.message || "");
}

async function completeDiscoverableLogin(credential, publicKey, enrollApp = false) {
  const encodedCredential = serializeCredential(credential);
  const encodedChallenge = arrayBufferToBase64Url(publicKey.challenge);
  const userHandle = credential.response?.userHandle;
  const userHandleLength = userHandle ? userHandle.byteLength : 0;

  debugLog("Completing discoverable login", {
    credentialId: credential.id,
    credentialType: credential.type,
    userHandleLength,
    challengeLength: encodedChallenge.length,
    attestationLength: encodedCredential.length,
  });

  const encodedUserHandle = userHandleLength ? arrayBufferToBase64Url(userHandle) : "";
  let accessToken;
  try {
    accessToken = await finalizeAuth(encodedCredential, encodedUserHandle, encodedChallenge, enrollApp);
  } catch (error) {
    if (isUnknownCredentialError(error)) {
      await signalUnknownPasskey(credential.id);
    }
    throw error;
  }
  saveCredentialHint(getCurrentAppConfig().appId, credential.id);
  state.ignoreCredentialHint = false;
  signalAcceptedPasskeys(accessToken);
  return true;
}

async function tryWebAuthnLogin(enrollApp = false) {
  cancelConditionalLogin();
  const encodedChallenge = await startAuth();
  const options = decodePublicKeyOptions(encodedChallenge);
  debugLog("Calling navigator.credentials.get", {
    hasAllowCredentials: Boolean(options.publicKey.allowCredentials?.length),
    rpId: options.publicKey.rpId || "",
    userVerification: options.publicKey.userVerification || "",
  });
  const credential = await navigator.credentials.get({ publicKey: options.publicKey });

  if (!credential) {
    debugLog("navigator.credentials.get returned no credential");
    return "";
  }

  debugLog("navigator.credentials.get returned credential", {
    credentialId: credential.id,
    authenticatorAttachment: credential.authenticatorAttachment || "",
    hasUserHandle: Boolean(credential.response?.userHandle),
    userHandleLength: credential.response?.userHandle
      ? credential.response.userHandle.byteLength
      : 0,
  });

  return completeDiscoverableLogin(credential, options.publicKey, enrollApp);
}

function cancelConditionalLogin() {
  if (state.conditionalAbort) {
    state.conditionalAbort.abort();
    state.conditionalAbort = null;
  }
}

// Passkey autofill: the browser offers the person's passkeys in the page's autofill list, so
// no dialog opens until they pick one. The request never resolves if they have none or
// ignore it, so the explicit Login button must always stay available beside it.
async function startConditionalLogin() {
  const field = byId("passkeyAutofill");
  if (!field || state.conditionalAbort || state.webAuthnAuthInProgress) return false;
  const capabilities = await getCapabilities();
  if (!capabilities.conditionalGet) return false;
  const controller = new AbortController();
  state.conditionalAbort = controller;
  try {
    const options = decodePublicKeyOptions(await startAuth({ useHint: false }));
    const credential = await navigator.credentials.get({
      publicKey: options.publicKey,
      mediation: "conditional",
      signal: controller.signal,
    });
    if (!credential) return false;
    state.conditionalAbort = null;
    await completeDiscoverableLogin(credential, options.publicKey, false);
    renderAuthenticatedState(await getUserData());
    return true;
  } catch (error) {
    if (error?.name === "AbortError") return false;
    debugLog("Passkey autofill sign-in failed", { name: error?.name || "", message: error?.message || String(error) });
    if (isAppNotEnrolledError(error)) {
      window.askToConnectApp();
    }
    // Anything else stays quiet: this ran without the person asking, and the Login button is still there.
    return false;
  } finally {
    if (state.conditionalAbort === controller) state.conditionalAbort = null;
  }
}

function handleWebAuthnError(error, fallbackMessage) {
  if (error?.name === "NotAllowedError") {
    showTryAgainDialog("Passkey login was cancelled. Try signing in again or sign up with another email.");
    return;
  }

  if (error?.message) {
    loginWebAuthnPopUpError(error.message);
    return;
  }

  loginWebAuthnPopUpError(fallbackMessage);
}

function isWebAuthnCancellation(error) {
  return error?.name === "NotAllowedError";
}

function isDocumentReadyForWebAuthn() {
  return document.visibilityState === "visible" && document.hasFocus();
}

function scheduleAutoWebAuthnAuth() {
  if (state.autoRetryHandlerAttached) {
    return;
  }

  state.autoRetryHandlerAttached = true;

  const retryWhenReady = () => {
    if (!isDocumentReadyForWebAuthn()) {
      return;
    }

    state.autoRetryHandlerAttached = false;
    window.removeEventListener("focus", retryWhenReady);
    document.removeEventListener("visibilitychange", retryWhenReady);
    window.checkIfWebAuthnLoggedIn(true);
  };

  window.addEventListener("focus", retryWhenReady);
  document.addEventListener("visibilitychange", retryWhenReady);
}

async function restoreSessionIfPresent() {
  try {
    if (!getStoredAccessToken()) return false;
    debugLog("Restoring the current app session");
    const userData = await getUserData();
    const currentApp = getCurrentAppConfig();
    const storedAppId = getStoredSessionAppId();

    if (!storedAppId) {
      debugLog("Stored token has no app scope");
      await logoutSession();
      renderLoggedOutState();
      setStatusLine(`Please sign in again for ${currentApp.label}.`);
      return false;
    }

    if (storedAppId !== currentApp.appId) {
      const scopedApp = getAppConfigById(storedAppId);
      debugLog("Stored token is scoped to another app", {
        requestedAppId: currentApp.appId,
        storedAppId,
      });
      await logoutSession();
      clearStoredSessionAppId();
      renderLoggedOutState();
      setStatusLine(
        scopedApp
          ? `Current session is for ${scopedApp.label}. Sign in again for ${currentApp.label}.`
          : `Please sign in again for ${currentApp.label}.`
      );
      return false;
    }

    renderAuthenticatedState(userData);
    return true;
  } catch (error) {
    debugLog("Stored app token not usable", {
      message: error?.message || String(error),
    });
    renderLoggedOutState();
    return false;
  }
}

// What this browser can do, from getClientCapabilities() where it exists and from the older
// feature checks elsewhere. Sign-in needs only WebAuthn itself: a security key or a phone
// over hybrid works as well as a platform authenticator.
async function getCapabilities() {
  const capabilities = {
    webauthn: Boolean(window.PublicKeyCredential),
    conditionalGet: false,
    platformAuthenticator: false,
    hybridTransport: false,
    relatedOrigins: null, // null: the browser cannot tell, so try and handle SecurityError
    signals: false,
  };
  if (!capabilities.webauthn) return capabilities;
  try {
    if (typeof PublicKeyCredential.getClientCapabilities === "function") {
      const reported = (await PublicKeyCredential.getClientCapabilities()) || {};
      capabilities.conditionalGet = Boolean(reported.conditionalGet);
      capabilities.platformAuthenticator = Boolean(reported.userVerifyingPlatformAuthenticator);
      capabilities.hybridTransport = Boolean(reported.hybridTransport);
      capabilities.relatedOrigins = typeof reported.relatedOrigins === "boolean" ? reported.relatedOrigins : null;
      capabilities.signals = Boolean(reported.signalAllAcceptedCredentials);
    } else {
      capabilities.conditionalGet = Boolean(
        PublicKeyCredential.isConditionalMediationAvailable
          && (await PublicKeyCredential.isConditionalMediationAvailable())
      );
      capabilities.platformAuthenticator = Boolean(
        PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable
          && (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable())
      );
      capabilities.signals = signalsAvailable("signalAllAcceptedCredentials");
    }
  } catch (error) {
    debugLog("Capability detection failed", { message: error?.message || String(error) });
  }
  return capabilities;
}

// A page outside the RP ID's own domains needs related-origin support from the browser;
// without it the app should open the hosted popup on pitican.com instead.
async function needsPopupFallback() {
  if (RP_ORIGINS.includes(window.location.origin)) return false;
  const capabilities = await getCapabilities();
  return capabilities.webauthn && capabilities.relatedOrigins === false;
}
window.bouncerCapabilities = getCapabilities;
window.bouncerNeedsPopupFallback = needsPopupFallback;

window.checkIfWebAuthnLoggedIn = async function checkIfWebAuthnLoggedIn(triggerAuth = false, { enrollApp = false } = {}) {
  debugLog("checkIfWebAuthnLoggedIn called", { triggerAuth, enrollApp });
  const alreadyLoggedIn = await restoreSessionIfPresent();
  if (alreadyLoggedIn) {
    debugLog("Session restore succeeded");
    return true;
  }

  renderLoggedOutState();

  const capabilities = await getCapabilities();
  if (!capabilities.webauthn) {
    debugLog("Browser does not support WebAuthn");
    loginWebAuthnPopUpError("This browser does not support passkeys.");
    return false;
  }

  if (!triggerAuth && capabilities.conditionalGet && byId("passkeyAutofill")) {
    // No dialog on page load: passkeys are offered in the autofill list instead.
    startConditionalLogin();
    return false;
  }

  if (!triggerAuth) {
    if (isDocumentReadyForWebAuthn()) {
      debugLog("Page is ready, attempting transparent passkey login");
      triggerAuth = true;
    } else {
      debugLog("Page not focused/visible yet, scheduling auto retry");
      scheduleAutoWebAuthnAuth();
      return false;
    }
  }

  if (!isDocumentReadyForWebAuthn()) {
    debugLog("Cannot start passkey login while page is not focused/visible");
    scheduleAutoWebAuthnAuth();
    return false;
  }

  if (state.webAuthnAuthInProgress) {
    debugLog("Passkey login already in progress");
    return false;
  }

  state.webAuthnAuthInProgress = true;

  try {
    const didAuthenticate = await tryWebAuthnLogin(enrollApp);
    if (!didAuthenticate) {
      debugLog("Passkey login returned without a token");
      showDialog(".popupTryagain");
      return false;
    }

    debugLog("Passkey login completed, fetching authenticated state");
    const userData = await getUserData();
    renderAuthenticatedState(userData);
    hideDialog(".popupWebauthn");
    hideDialog(".popupTryagain");
    return true;
  } catch (error) {
    debugLog("Passkey login failed", {
      name: error?.name || "",
      message: error?.message || String(error),
    });
    if (isAppNotEnrolledError(error)) {
      window.askToConnectApp();
      return false;
    }
    if (error?.name === "SecurityError" && (await needsPopupFallback())) {
      loginWebAuthnPopUpError("This browser cannot use your Bouncer passkey on this site. Open the sign-in window on pitican.com instead.");
      return false;
    }
    handleWebAuthnError(error, "Passkey login failed.");
    if (!isWebAuthnCancellation(error)) {
      showTryAgainDialog(error?.message || "Passkey login failed.");
    }
    return false;
  } finally {
    state.webAuthnAuthInProgress = false;
  }
};

window.onpaintWebAuthnLoggedIn = function onpaintWebAuthnLoggedIn() {
  window.checkIfWebAuthnLoggedIn(false);
};

// A verified passkey proves whose account this is, but connecting a new app to it needs the
// person's agreement. The next ceremony carries that agreement as enrollApp.
window.askToConnectApp = function askToConnectApp() {
  cancelConditionalLogin();
  const text = byId("connectAppText");
  if (text) {
    text.textContent = `Connect ${getCurrentAppConfig().label} to your Bouncer account?`;
  }
  showDialog(".popupConnectApp");
};

window.confirmConnectApp = function confirmConnectApp() {
  hideDialog(".popupConnectApp");
  return window.checkIfWebAuthnLoggedIn(true, { enrollApp: true });
};

window.closeConnectApp = function closeConnectApp() {
  hideDialog(".popupConnectApp");
};

async function completeEnrollmentCredential(response, expectedAppId, hints = []) {
  if (!expectedAppId || response.getAppid() !== expectedAppId) {
    throw new Error("This enrollment link belongs to another app.");
  }
  const flow = response.getFlow();
  if (flow !== "create" && flow !== "enroll" && flow !== "recover") {
    throw new Error("Unsupported Bouncer enrollment response. Please update the app.");
  }
  state.lastEnrollmentFlow = flow;
  // create and recover both make a passkey; only enroll signs in with an existing one.
  const makesPasskey = flow !== "enroll";
  const options = decodePublicKeyOptions(response.getChallenge(), {
    create: makesPasskey,
    hints: makesPasskey ? hints : [],
  });
  const credential = makesPasskey
    ? await navigator.credentials.create({ publicKey: options.publicKey })
    : await navigator.credentials.get({ publicKey: options.publicKey });
  if (!credential) throw new Error("Passkey operation was cancelled.");
  return credential;
}

window.lastEnrollmentFlow = () => state.lastEnrollmentFlow;

// `sessionToken` (optional) is an access token of the same account from a device that is
// already signed in; it lets "recover" add a passkey without waiting out the recovery delay.
// `hints` (optional) steer the browser's dialog, for example ["security-key"].
window.consideringEntry = async function consideringEntry(entryToken, hasPlatformAuthn, recover = false, sessionToken = "", hints = []) {
  try {
    debugLog("Starting passkey registration flow", {
      hasPlatformAuthn,
      tokenLength: entryToken ? entryToken.length : 0,
    });
    addDebugLine("Starting passkey registration");

    const request = new ValidateSignupRequest();
    request.setToken(entryToken);
    request.setPlatformauth(hasPlatformAuthn);
    request.setPlatformtype("web");
    request.setRecover(Boolean(recover));
    if (sessionToken) request.setSessiontoken(sessionToken);
    state.lastEnrollmentFlow = "";

    const response = await regRWService.considerEntry(request, {});
    const registrationHandle = response.getRegistrationhandle();
    const credential = await completeEnrollmentCredential(response, getCurrentAppConfig().appId, hints);

    debugLog("navigator.credentials.create returned credential", {
      credentialId: credential.id,
      authenticatorAttachment: credential.authenticatorAttachment || "",
    });

    const accessToken = await finalizeSignup(serializeCredential(credential), registrationHandle);
    saveCredentialHint(getCurrentAppConfig().appId, credential.id);
    signalAcceptedPasskeys(accessToken);

    const registerTitle = byId("registerTitle");
    if (registerTitle) {
      registerTitle.textContent = "Registration complete";
    }
    addDebugLine("Registration complete. You can return to the home page.");
    return true;
  } catch (error) {
    debugLog("Passkey registration failed", {
      name: error?.name || "",
      message: error?.message || String(error),
    });
    const registerTitle = byId("registerTitle");
    if (registerTitle) {
      registerTitle.textContent = "Registration failed";
    }
    if (isWebAuthnCancellation(error)) {
      addDebugLine("Passkey registration was cancelled or lost focus. Click \"Create passkey\" to try again.");
    } else if (error?.name === "InvalidStateError" && state.lastEnrollmentFlow === "recover") {
      addDebugLine("This device already has a passkey for this account. Go back and sign in with it.");
    } else if (/RECOVERY_DELAYED/.test(error?.message || "")) {
      const when = recoveryTimeFromError(error);
      addDebugLine(
        when
          ? `For your protection a new passkey cannot be added by email alone before ${when.toLocaleString()}. Request a new link then. If you are signed in on another device you can add it there at once.`
          : "For your protection a new passkey cannot be added by email alone yet. Try again later."
      );
    } else {
      addDebugLine(error?.message || "Registration failed");
    }
    return false;
  }
};

window.removingEntry = async function removingEntry(token) {
  try {
    const removed = await deleteUser(token);
    clearLegacyAuthState();
    clearStoredSessionAppId();
    return {
      success: true,
      message: removed.email ? `[${removed.email}] was removed.` : "The removal completed.",
      userHandle: removed.userHandle || "",
    };
  } catch (error) {
    return {
      success: false,
      message: error?.message || "Unable to remove user",
    };
  }
};

window.loginWebAuthnPopUpError = function loginWebAuthnPopUpError(message) {
  showDialog(".popupWebauthn");
  setError(".errorWebauthnLogin", `Error [${message}]`);
};

window.loginWebAuthnPopUp = function loginWebAuthnPopUp() {
  showDialog(".popupWebauthn");
};

window.toggleWebAuthnLogin = function toggleWebAuthnLogin() {
  clearError(".errorWebauthnLogin");
  hideDialog(".popupWebauthn");
  hideDialog(".popupTryagain");

  const button = byId("showWebAuthnLogin");
  if (!button) {
    return;
  }

  if (button.textContent === "Logout") {
    byId("userTitle").textContent = "Not logged in";
    window.logoutWebAuthnSession();
    return;
  }

  window.checkIfWebAuthnLoggedIn(true).then((success) => {
    if (!success && !query(".popupTryagain")?.classList.contains("active")) {
      showDialog(".popupWebauthn");
    }
  });
};

window.closeWebAuthnLogin = function closeWebAuthnLogin() {
  clearError(".errorWebauthnLogin");
  hideDialog(".popupWebauthn");
};

window.sendWebAuthnLogin = function sendWebAuthnLogin() {
  clearError(".errorWebauthnLogin");
  hideDialog(".popupWebauthn");
  hideDialog(".popupTryagain");
  window.checkIfWebAuthnLoggedIn(true);
};

window.toggleReqSignup = function toggleReqSignup() {
  showDialog(".popupReqSignup");
};

window.closeReqSignup = function closeReqSignup() {
  clearError(".errorReqSignup");
  hideDialog(".popupReqSignup");
};

window.sendReqSignup = async function sendReqSignup() {
  const email = byId("emailReqSignup")?.value.trim().toLowerCase();
  if (!email) {
    setError(".errorReqSignup", "Error [Check email]");
    return;
  }

  clearError(".errorReqSignup");

  try {
    await requestSignup(email);
    hideDialog(".popupReqSignup");
    setStatusLine("Check your email for the registration link.");
  } catch (error) {
    setError(".errorReqSignup", `Error [${error?.message || "Unable to send signup email"}]`);
  }
};

window.toggleReqTryagain = function toggleReqTryagain() {
  showDialog(".popupTryagain");
};

window.closeReqTryagain = function closeReqTryagain() {
  clearError(".errorTryagain");
  hideDialog(".popupTryagain");
};

window.sendReqTryagain = function sendReqTryagain() {
  hideDialog(".popupTryagain");
  window.checkIfWebAuthnLoggedIn(true);
};

window.useDifferentPasskey = function useDifferentPasskey() {
  state.ignoreCredentialHint = true;
  hideDialog(".popupTryagain");
  window.checkIfWebAuthnLoggedIn(true);
};

window.closeReqTryagainAndSignup = function closeReqTryagainAndSignup() {
  hideDialog(".popupTryagain");
  showDialog(".popupReqSignup");
};

window.toggleSettings = function toggleSettings() {
  const colorPicker = byId("colorPicker");
  if (colorPicker) {
    updateColorPickerPreview(colorPicker.value);
  }
  // An agent token can only be minted for the app the person is signed in to (the server refuses
  // another app with "Cross-app delegation is not allowed"), so offer that app's ID.
  const agentTarget = byId("agentTargetAppId");
  if (agentTarget) {
    agentTarget.value = getCurrentAppConfig().appId;
  }
  const passkeyContainer = byId("passkeyListContainer");
  if (passkeyContainer && getStoredAccessToken()) {
    window.renderPasskeys(passkeyContainer, getStoredAccessToken(), { onAdd: window.addAnotherPasskey });
  }
  showDialog("#settingsPopup");
};

window.closeSettings = function closeSettings() {
  clearError(".errorSettings");
  hideDialog("#settingsPopup");
};

window.saveColor = async function saveColor() {
  const selectedColor = byId("colorPicker")?.value;

  try {
    await persistBackground(selectedColor);
    setBodyBackground(selectedColor);
    window.closeSettings();
  } catch (error) {
    setError(".errorSettings", `Error [${error?.message || "Unable to save settings"}]`);
  }
};

window.requestDeletionLink = async function requestDeletionLink() {
  clearError(".errorSettings");
  try {
    await requestRemovalLink(true);
    window.closeSettings();
    setStatusLine("Check your email for the account deletion link.");
  } catch (error) {
    setError(".errorSettings", `Error [${error?.message || "Unable to send delete link"}]`);
  }
};

window.requestDisconnectLink = async function requestDisconnectLink() {
  clearError(".errorSettings");
  try {
    await requestRemovalLink(false);
    window.closeSettings();
    setStatusLine("Check your email for the link to disconnect this app.");
  } catch (error) {
    setError(".errorSettings", `Error [${error?.message || "Unable to send disconnect link"}]`);
  }
};

window.removalScopeFromToken = function removalScopeFromToken(token) {
  return decodeJwtPayload(token)?.scope === "account" ? "account" : "app";
};

window.generateAgentToken = async function generateAgentToken() {
  clearError(".errorSettings");
  const label = byId("agentLabel")?.value?.trim();
  const targetAppId = byId("agentTargetAppId")?.value?.trim();

  if (!label) {
    setError(".errorSettings", "Agent label is required.");
    return;
  }
  if (!targetAppId) {
    setError(".errorSettings", "Target app ID is required.");
    return;
  }

  try {
    const request = new GenerateAgentTokenRequest();
    request.setToken(getStoredAccessToken());
    request.setAgentlabel(label);
    request.setTargetappid(targetAppId);
    const response = await regRWService.generateAgentToken(request, {});
    const token = response.getAgenttoken();
    const resultEl = byId("agentTokenResult");
    const outputEl = byId("agentTokenOutput");
    if (outputEl) {
      outputEl.value = token;
    }
    if (resultEl) {
      resultEl.hidden = false;
    }
    const publicKeyEl = byId("agentPublicKeyOutput");
    if (publicKeyEl) {
      try {
        const keyResponse = await fetch("/jwt-public-key.pem");
        publicKeyEl.value = keyResponse.ok ? await keyResponse.text() : "(could not fetch public key)";
      } catch {
        publicKeyEl.value = "(could not fetch public key)";
      }
    }
  } catch (error) {
    setError(".errorSettings", `Error [${error?.message || "Unable to generate token"}]`);
  }
};

window.copyAgentToken = function copyAgentToken() {
  const outputEl = byId("agentTokenOutput");
  if (!outputEl?.value) {
    return;
  }
  navigator.clipboard.writeText(outputEl.value).catch(() => {
    outputEl.select();
    document.execCommand("copy");
  });
};

window.copyAgentPublicKey = function copyAgentPublicKey() {
  const outputEl = byId("agentPublicKeyOutput");
  if (!outputEl?.value) {
    return;
  }
  navigator.clipboard.writeText(outputEl.value).catch(() => {
    outputEl.select();
    document.execCommand("copy");
  });
};

window.updateColorPickerPreview = updateColorPickerPreview;
window.signalAccountDeleted = signalAccountDeleted;

window.logoutWebAuthnSession = async function logoutWebAuthnSession() {
  try {
    await logoutSession();
  } catch (error) {
    debugLog("Backend logout failed", {
      message: error?.message || String(error),
    });
  }
  clearLegacyAuthState();
  clearStoredSessionAppId();
  renderLoggedOutState();
};

window.PopupRegisterClient = regROService;
window.PopupRegRWClient = regRWService;
window.PopupToArrayBuffer = toArrayBuffer;
window.PopupArrayBufferToBase64Url = arrayBufferToBase64Url;
window.PopupPublicKeyCredentialToJSON = publicKeyCredentialToJSON;
window.PopupStringToBase64Url = stringToBase64Url;
window.PopupDecodeBase64ToString = decodeBase64ToString;
window.PopupDecodePublicKeyOptions = decodePublicKeyOptions;
window.PopupSerializeCredential = serializeCredential;
window.PopupValidateSignupRequest = ValidateSignupRequest;
window.PopupFinaliseSignupRequest = FinaliseSignupRequest;
window.PopupBeginAuthRequest = BeginAuthRequest;
window.PopupFinaliseAuthRequest = FinaliseAuthRequest;

window.popupRegisterFlow = async function popupRegisterFlow(appId, entryToken, recover = false, sessionToken = "") {
  const hasPlatformAuthn = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();

  const req = new ValidateSignupRequest();
  req.setToken(entryToken);
  req.setPlatformauth(hasPlatformAuthn);
  req.setPlatformtype("web");
  req.setRecover(Boolean(recover));
  if (sessionToken) req.setSessiontoken(sessionToken);
  setProtoStringFieldIfAvailable(req, "setAppid", appId);

  const resp = await regRWService.considerEntry(req, {});
  const regHandle = resp.getRegistrationhandle();
  const credential = await completeEnrollmentCredential(resp, appId);

  const att = serializeCredential(credential);

  const finReq = new FinaliseSignupRequest();
  finReq.setAttestation(att);
  finReq.setRegistrationhandle(regHandle);
  setProtoStringFieldIfAvailable(finReq, "setAppid", appId);

  const finResp = await regRWService.addEntry(finReq, {});
  saveCredentialHint(appId, credential.id);
  return finResp.getAccesstoken();
};

// `enrollApp` is true only after the person agreed on screen to connect the app.
window.popupLoginFlow = async function popupLoginFlow(appId, useHint = true, enrollApp = false) {
  const req = new BeginAuthRequest();
  setProtoStringFieldIfAvailable(req, "setAppid", appId);

  const hint = !useHint ? "" : getCredentialHint(appId);
  if (hint) req.setCredentialid(hint);
  const resp = await regROService.startAuthn(req, {});
  const challenge = resp.getChallenge();
  const pkOpts = decodePublicKeyOptions(challenge);

  const credential = await navigator.credentials.get({ publicKey: pkOpts.publicKey });
  if (!credential) throw new Error("Passkey login returned no credential.");

  const encodedCred = serializeCredential(credential);
  const encodedChallenge = arrayBufferToBase64Url(pkOpts.publicKey.challenge);
  const userHandle = credential.response?.userHandle;

  const encodedHandle = userHandle?.byteLength ? arrayBufferToBase64Url(userHandle) : "";

  const finReq = new FinaliseAuthRequest();
  finReq.setAttestation(encodedCred);
  finReq.setUserhandle(encodedHandle);
  finReq.setChallenge(encodedChallenge);
  finReq.setEnrollapp(Boolean(enrollApp));

  let finResp;
  try {
    finResp = await regROService.endAuthn(finReq, {});
  } catch (error) {
    if (isUnknownCredentialError(error)) await signalUnknownPasskey(credential.id);
    throw error;
  }
  saveCredentialHint(appId, credential.id);
  return finResp.getAccesstoken();
};
window.isAppNotEnrolledError = isAppNotEnrolledError;

// ---- Passkey management -------------------------------------------------------------------

function recoveryTimeFromError(error) {
  const match = /RECOVERY_DELAYED: (\d+)/.exec(error?.message || "");
  return match ? new Date(Number(match[1]) * 1000) : null;
}

async function listPasskeys(token) {
  const request = new ListPasskeysRequest();
  request.setToken(token);
  const response = await regRWService.listPasskeys(request, {});
  return {
    userHandle: response.getUserhandle(),
    pendingRecoveryAt: Number(response.getPendingrecoveryat()),
    passkeys: response.getPasskeysList().map((passkey) => ({
      credentialId: passkey.getCredentialid(),
      label: passkey.getLabel(),
      aaguid: passkey.getAaguid(),
      attachment: passkey.getAttachment(),
      backupEligible: passkey.getBackupeligible(),
      backupState: passkey.getBackupstate(),
      transports: passkey.getTransportsList(),
      createdAt: Number(passkey.getCreatedat()),
      lastUsedAt: Number(passkey.getLastusedat()),
    })),
  };
}

async function removePasskey(token, credentialId) {
  const request = new RemovePasskeyRequest();
  request.setToken(token);
  request.setCredentialid(credentialId);
  const response = await regRWService.removePasskey(request, {});
  return response.getRemaining();
}

async function cancelRecovery(token) {
  const request = new CancelRecoveryRequest();
  request.setToken(token);
  await regRWService.cancelRecovery(request, {});
}

// Adding a passkey from a signed-in device still needs the email link (the mailbox stays the
// root of trust). The signed-in device hands its access token to the page the link opens, for
// at most 30 minutes, so that page can skip the recovery delay.
const ADD_PASSKEY_HANDOFF_KEY = "bouncer:addPasskeyHandoff";

function stashSessionForAddPasskey(token) {
  try {
    localStorage.setItem(ADD_PASSKEY_HANDOFF_KEY, JSON.stringify({ token, expires: Date.now() + 30 * 60 * 1000 }));
  } catch { /* without it the delay applies */ }
}

window.takeSessionForAddPasskey = function takeSessionForAddPasskey() {
  try {
    const raw = localStorage.getItem(ADD_PASSKEY_HANDOFF_KEY);
    localStorage.removeItem(ADD_PASSKEY_HANDOFF_KEY);
    if (!raw) return "";
    const handoff = JSON.parse(raw);
    return handoff.expires > Date.now() && typeof handoff.token === "string" ? handoff.token : "";
  } catch {
    return "";
  }
};

async function requestAddPasskeyLink(appId, token) {
  const request = new UserDataRequest();
  request.setToken(token);
  const email = (await weberService.getUserData(request, {})).getEmail();
  stashSessionForAddPasskey(token);
  const signup = new SignupRequest();
  signup.setEmail(email.toLowerCase());
  setProtoStringFieldIfAvailable(signup, "setAppid", appId);
  await regRWService.requestEntry(signup, {});
  return email;
}

window.addAnotherPasskey = async function addAnotherPasskey() {
  const appConfig = getCurrentAppConfig();
  const email = await requestAddPasskeyLink(appConfig.appId, getStoredAccessToken());
  setStatusLine(`Check ${email} for the link that adds a passkey on this device.`);
};

function describePasskey(passkey) {
  const kind = passkey.backupEligible
    ? (passkey.backupState ? "synced to your account" : "can sync, not backed up yet")
    : "this device only";
  const added = passkey.createdAt ? `added ${new Date(passkey.createdAt * 1000).toLocaleDateString()}` : "";
  const used = passkey.lastUsedAt ? `last used ${new Date(passkey.lastUsedAt * 1000).toLocaleDateString()}` : "never used";
  return [kind, added, used].filter(Boolean).join(", ");
}

// Fills `container` with the account's passkeys, the pending-recovery notice and the "add a
// passkey" nudge. Built with textContent only, so nothing the server returns is parsed as HTML.
// options.onAdd: called when the person asks to add a passkey. options.onChanged: called after a change.
window.renderPasskeys = async function renderPasskeys(container, token, options = {}) {
  container.textContent = "";
  const add = (tag, text, className) => {
    const element = document.createElement(tag);
    if (text) element.textContent = text;
    if (className) element.className = className;
    container.appendChild(element);
    return element;
  };
  let list;
  try {
    list = await listPasskeys(token);
  } catch (error) {
    add("p", `Could not load your passkeys: ${error?.message || "unknown error"}`, "passkeyError");
    return;
  }

  if (list.pendingRecoveryAt) {
    const notice = add("div", "", "passkeyNotice");
    const text = document.createElement("p");
    text.textContent = `Someone asked to add a passkey to your account by email. It cannot be added before ${new Date(list.pendingRecoveryAt * 1000).toLocaleString()}. If this was not you, cancel it.`;
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.textContent = "Cancel the request";
    cancel.onclick = async () => {
      cancel.disabled = true;
      try {
        await cancelRecovery(token);
        await window.renderPasskeys(container, token, options);
      } catch (error) {
        text.textContent = `Could not cancel: ${error?.message || "unknown error"}`;
        cancel.disabled = false;
      }
    };
    notice.append(text, cancel);
  }

  const items = add("ul", "", "passkeyList");
  for (const passkey of list.passkeys) {
    const item = document.createElement("li");
    const name = document.createElement("strong");
    name.textContent = passkey.label || "Passkey";
    const detail = document.createElement("span");
    detail.textContent = ` - ${describePasskey(passkey)} `;
    item.append(name, detail);
    const remove = document.createElement("button");
    remove.type = "button";
    remove.textContent = "Remove";
    remove.disabled = list.passkeys.length < 2;
    remove.title = list.passkeys.length < 2 ? "The last passkey cannot be removed" : "";
    remove.onclick = async () => {
      if (!window.confirm("Remove this passkey? It will no longer sign in to any app.")) return;
      remove.disabled = true;
      try {
        await removePasskey(token, passkey.credentialId);
        await signalAcceptedPasskeys(token);
        await window.renderPasskeys(container, token, options);
        if (options.onChanged) options.onChanged();
      } catch (error) {
        detail.textContent = ` - could not remove: ${error?.message || "unknown error"}`;
        remove.disabled = false;
      }
    };
    item.appendChild(remove);
    items.appendChild(item);
  }

  if (list.passkeys.length < 2) {
    add("p", "You have only one passkey. Add another, on a different device or a security key, so losing this one cannot lock you out.", "passkeyNudge");
  }
  if (options.onAdd) {
    const addButton = add("button", "Add a passkey", "passkeyAdd");
    addButton.type = "button";
    addButton.onclick = async () => {
      addButton.disabled = true;
      try {
        await options.onAdd();
      } catch (error) {
        add("p", `Could not start: ${error?.message || "unknown error"}`, "passkeyError");
      }
      addButton.disabled = false;
    };
  }
};

window.popupAddPasskey = (appId, token) => requestAddPasskeyLink(appId, token);
window.popupListPasskeysUi = (container, appId, token) =>
  window.renderPasskeys(container, token, {
    onAdd: async () => {
      const email = await requestAddPasskeyLink(appId, token);
      const note = document.createElement("p");
      note.textContent = `Check ${email} for the link that adds a passkey on the device you open it on.`;
      container.appendChild(note);
    },
  });

const MODAL_SELECTORS = [".popupReqSignup", ".popupWebauthn", ".popupTryagain", ".popupConnectApp", "#settingsPopup"];

function closeAllDialogs() {
  for (const selector of MODAL_SELECTORS) hideDialog(selector);
  for (const selector of [".errorReqSignup", ".errorWebauthnLogin", ".errorTryagain", ".errorSettings"]) clearError(selector);
}

window.selectApp = async function selectApp(appId) {
  if (!APP_CONFIGS[appId] || appId === currentAppId) {
    renderAppChrome();
    return;
  }
  cancelConditionalLogin();
  state.ignoreCredentialHint = false;
  closeAllDialogs();
  const agentResult = byId("agentTokenResult");
  if (agentResult) agentResult.hidden = true;
  const agentOutput = byId("agentTokenOutput");
  if (agentOutput) agentOutput.value = "";
  setCurrentApp(appId);
  history.replaceState(null, "", `?app=${encodeURIComponent(appId)}`);
  renderLoggedOutState();
  await window.checkIfWebAuthnLoggedIn(false);
};

// Buttons name what they do in data-action; markup carries no script, so a strict CSP can hold.
const PAGE_ACTIONS = new Set([
  "toggleWebAuthnLogin", "closeWebAuthnLogin", "sendWebAuthnLogin",
  "toggleReqSignup", "closeReqSignup", "sendReqSignup",
  "sendReqTryagain", "useDifferentPasskey", "closeReqTryagainAndSignup", "closeReqTryagain",
  "confirmConnectApp", "closeConnectApp",
  "toggleSettings", "closeSettings", "saveColor", "generateAgentToken", "copyAgentToken", "copyAgentPublicKey",
  "requestDisconnectLink", "requestDeletionLink",
]);

function initSimulatorPage() {
  const select = byId("appSelect");
  if (select) {
    for (const app of APP_CATALOG) {
      const option = document.createElement("option");
      option.value = app.appId;
      option.textContent = app.label;
      select.appendChild(option);
    }
    select.addEventListener("change", () => window.selectApp(select.value));
  }
  document.addEventListener("click", (event) => {
    const target = event.target.closest("[data-action]");
    if (target && PAGE_ACTIONS.has(target.dataset.action)) window[target.dataset.action]();
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeAllDialogs();
  });
  byId("emailReqSignup")?.addEventListener("keydown", (event) => {
    if (event.key === "Enter") window.sendReqSignup();
  });
  byId("colorPicker")?.addEventListener("input", (event) => updateColorPickerPreview(event.target.value));
  renderLoggedOutState();
  renderAppChrome();
  window.checkIfWebAuthnLoggedIn(false);
}

// The link in an email carries a one-time token in the fragment; take it out of the address bar.
function takeTokenFromLocation() {
  const raw = window.location.hash.length > 1 ? window.location.hash.slice(1) : window.location.search;
  const token = new URLSearchParams(raw).get("token");
  history.replaceState(null, "", window.location.pathname);
  return token;
}

// The link belongs to one app, named inside the token.
function adoptAppOfToken(token) {
  const appId = decodeJwtPayload(token)?.app_id;
  if (appId) currentAppId = appId;
  const appName = byId("appName");
  if (appName) appName.textContent = getCurrentAppConfig().label;
}

function initRegisterPage() {
  const token = takeTokenFromLocation();
  const title = byId("registerTitle");
  const status = byId("registerStatus");
  const button = byId("startRegistrationButton");
  const securityKeyButton = byId("securityKeyButton");
  const recoverButton = byId("recoverButton");

  if (!token) {
    status.textContent = "The registration link is missing its token.";
    return;
  }
  adoptAppOfToken(token);

  // Set when this browser asked for the link while signed in: adding a passkey then skips the
  // recovery delay. Absent when the link is opened anywhere else.
  const signedInToken = window.takeSessionForAddPasskey();
  if (signedInToken) title.textContent = "Add a passkey";

  const fail = () => {
    title.textContent = "Registration failed";
    status.textContent = "Registration did not complete. Check the message below and try again.";
    button.textContent = "Try again";
    button.disabled = false;
    securityKeyButton.disabled = false;
    recoverButton.disabled = false;
    // An existing account asks for its old passkey first; offer a new one if that fails.
    recoverButton.hidden = window.lastEnrollmentFlow() !== "enroll";
  };

  const platformCheck = window.PublicKeyCredential && PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable
    ? PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
    : Promise.resolve(false);
  platformCheck.then((available) => {
    status.textContent = available
      ? "This device has a platform authenticator. Click below to create your passkey."
      : "No platform authenticator was detected. Click below to continue with another authenticator if available.";
    button.disabled = false;
    securityKeyButton.disabled = false;

    const run = async (recover, hints) => {
      button.disabled = true;
      securityKeyButton.disabled = true;
      recoverButton.disabled = true;
      recoverButton.hidden = true;
      status.textContent = recover ? "Creating a new passkey for your account..." : "Working on your passkey...";
      try {
        const done = await window.consideringEntry(token, available, recover || Boolean(signedInToken), signedInToken, hints || []);
        if (!done) return fail();
        title.textContent = "Passkey ready";
        status.textContent = "Passkey registration complete. You can return to the home page and sign in automatically.";
        button.textContent = "Create another passkey";
        button.disabled = false;
        securityKeyButton.disabled = false;
      } catch (error) {
        console.error(error);
        fail();
      }
    };
    button.addEventListener("click", () => run(false));
    securityKeyButton.addEventListener("click", () => run(false, ["security-key"]));
    recoverButton.addEventListener("click", () => run(true));
  }).catch((error) => {
    console.error(error);
    status.textContent = "Unable to check authenticator support on this device.";
  });
}

function initUnregisterPage() {
  setBodyBackground(DEFAULT_BACKGROUND);
  const token = takeTokenFromLocation();
  const title = byId("unregisterTitle");
  const status = byId("unregisterStatus");
  const button = byId("confirmUnregisterButton");

  if (!token) {
    status.textContent = "The removal link is missing its token.";
    return;
  }
  adoptAppOfToken(token);

  const isAccount = window.removalScopeFromToken(token) === "account";
  status.textContent = isAccount
    ? "This link permanently deletes your Bouncer account and disconnects every app."
    : "This link disconnects this app from your Bouncer account. Your account and passkeys are kept.";
  button.disabled = false;
  button.addEventListener("click", async () => {
    button.disabled = true;
    status.textContent = isAccount ? "Deleting account..." : "Disconnecting app...";
    try {
      const response = await window.removingEntry(token);
      if (response && response.success) {
        title.textContent = isAccount ? "Account deleted" : "App disconnected";
        status.textContent = response.message;
        if (isAccount) {
          window.signalAccountDeleted(response.userHandle).then((signalResult) => {
            status.textContent = response.message + " " + signalResult.message;
          }).catch(() => {
            status.textContent = response.message + " Delete the saved passkey from your password manager because this account is no longer valid.";
          });
        }
        button.textContent = isAccount ? "Deleted" : "Disconnected";
      } else {
        title.textContent = "Unable to remove account";
        status.textContent = (response && response.message) || "Account removal did not complete.";
        button.textContent = "Try again";
        button.disabled = false;
      }
    } catch (error) {
      console.error(error);
      title.textContent = "Unable to remove account";
      status.textContent = "Account removal did not complete. Please try again.";
      button.textContent = "Try again";
      button.disabled = false;
    }
  });
}

const legacyTokenExpired = clearExpiredLegacyToken();

function initPage() {
  if (isHomePage()) {
    if (legacyTokenExpired) {
      clearLegacyAuthState();
    }
    initSimulatorPage();
  } else if (isRegisterPage()) {
    initRegisterPage();
  } else if (isUnregisterPage()) {
    initUnregisterPage();
  }
}

// Public app IDs are not authorization to send an identity token to an arbitrary origin.
const popupReturnOrigins = require("./app_return_origins.json");
window.popupReturnOriginAllowed = function(appId, origin) {
  try {
    return new URL(origin).origin === origin && (popupReturnOrigins[appId] || []).includes(origin);
  } catch { return false; }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initPage);
} else {
  initPage();
}
